import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import axios from 'axios'
import { VARIABLE_DEFINITIONS, resolveVariables } from '../utils/proposalVariables'
import {
  buildEstimateVariables,
  buildExecSummaryData,
  buildProjectData,
  buildScopeData,
  hasEstimateContract,
  mergeEstimateVariables,
} from '../utils/estimateContract'

// Minimal fallback — the full defaults come from the server module-registry endpoint
const FALLBACK_REGISTRY = [
  { id: 'cover', defaults: {} },
  { id: 'executiveSummary', defaults: {} },
  { id: 'intro', defaults: {} },
  { id: 'about', defaults: {} },
  { id: 'scope', defaults: {} },
  { id: 'startup', defaults: {} },
  { id: 'serviceArea', defaults: {} },
  { id: 'project', defaults: {} },
  { id: 'notes', defaults: {}, defaultEnabled: false },
  { id: 'terms', defaults: {} }
]

const ESTIMATE_OWNED_VARIABLES = new Set([
  'laborerRate',
  'irrigationRate',
  'sprayTechRate',
  'operatorRate',
  'craneOperatorRate',
  'emergencyRate',
  'maintenanceFrequency',
  'irrigationCheckFrequency',
  'preEmergentFrequency',
])

const moduleDefaultEnabled = (moduleDef) => moduleDef.defaultEnabled !== false

const ensureRegistryModules = (proposal, registry) => {
  if (!proposal || !Array.isArray(proposal.modules) || !Array.isArray(registry)) return proposal
  const existingIds = new Set(proposal.modules.map(m => m.moduleId))
  let nextOrder = proposal.modules.reduce((max, mod) => Math.max(max, Number(mod.order) || 0), -1) + 1
  const missingModules = registry
    .filter(moduleDef => moduleDef?.id && !existingIds.has(moduleDef.id))
    .map(moduleDef => ({
      moduleId: moduleDef.id,
      enabled: moduleDefaultEnabled(moduleDef),
      order: nextOrder++,
      overrides: {}
    }))

  if (!missingModules.length) return proposal
  return { ...proposal, modules: [...proposal.modules, ...missingModules] }
}

export function useProposal (proposalId, url) {
  const [proposal, setProposal] = useState(null)
  const [template, setTemplate] = useState(null)
  const [moduleRegistry, setModuleRegistry] = useState(FALLBACK_REGISTRY)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [dirty, setDirty] = useState(false)
  const saveTimer = useRef(null)
  const pendingSave = useRef(null)

  const proposalSaveUrl = useMemo(() => (
    proposalId && url ? `${url}/api/mtc-proposals/${proposalId}` : ''
  ), [proposalId, url])

  // Fetch module registry from server (for defaults)
  useEffect(() => {
    if (!url) return
    axios.get(`${url}/api/mtc-proposals/module-registry`)
      .then(res => { if (Array.isArray(res.data)) setModuleRegistry(res.data) })
      .catch(() => {})
  }, [url])

  // Fetch proposal from server
  const fetchProposal = useCallback(async () => {
    if (!proposalId || !url) return
    setLoading(true)
    try {
      const res = await axios.get(`${url}/api/mtc-proposals/${proposalId}`)
      setProposal(ensureRegistryModules(res.data, moduleRegistry))
      setTemplate(res.data.templateId || null)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch proposal:', err)
      setError('Failed to load proposal')
    } finally {
      setLoading(false)
    }
  }, [proposalId, url])

  useEffect(() => {
    if (proposalId) fetchProposal()
  }, [proposalId, fetchProposal])

  useEffect(() => {
    setProposal(prev => ensureRegistryModules(prev, moduleRegistry))
  }, [moduleRegistry])

  // Save proposal to server (debounced)
  // Does NOT overwrite local state — local edits are the source of truth while editing.
  // Only the initial fetch and forceSave set proposal from server.
  const saveProposal = useCallback(async (data) => {
    if (!proposalSaveUrl) return
    setSaving(true)
    try {
      await axios.put(proposalSaveUrl, data)
      if (pendingSave.current === data) pendingSave.current = null
      setDirty(false)
      setError(null)
    } catch (err) {
      console.error('Failed to save proposal:', err)
      setError('Failed to save')
    } finally {
      setSaving(false)
    }
  }, [proposalSaveUrl])

  const debouncedSave = useCallback((data) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    pendingSave.current = { ...(pendingSave.current || {}), ...data }
    saveTimer.current = setTimeout(() => {
      const payload = pendingSave.current
      pendingSave.current = null
      saveProposal(payload)
    }, 1500)
    setDirty(true)
  }, [saveProposal])

  const flushPendingSave = useCallback(() => {
    if (!proposalSaveUrl || !pendingSave.current) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    const payload = pendingSave.current
    pendingSave.current = null
    setDirty(false)

    if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
      window.fetch(proposalSaveUrl, {
        method: 'PUT',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {})
    } else {
      saveProposal(payload)
    }
  }, [proposalSaveUrl, saveProposal])

  useEffect(() => {
    const handlePageHide = () => flushPendingSave()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushPendingSave()
    }
    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      flushPendingSave()
    }
  }, [flushPendingSave])

  // Update a module override
  const updateModuleOverride = useCallback((moduleId, fieldName, value) => {
    setProposal(prev => {
      if (!prev) return prev
      const newModules = prev.modules.map(m => {
        if (m.moduleId !== moduleId) return m
        return { ...m, overrides: { ...m.overrides, [fieldName]: value } }
      })
      const updated = { ...prev, modules: newModules }
      debouncedSave({ modules: newModules })
      return updated
    })
  }, [debouncedSave])

  // Bulk update multiple fields on a module at once (avoids race conditions)
  const updateModuleOverrides = useCallback((moduleId, fieldsObj) => {
    setProposal(prev => {
      if (!prev) return prev
      const newModules = prev.modules.map(m => {
        if (m.moduleId !== moduleId) return m
        return { ...m, overrides: { ...m.overrides, ...fieldsObj } }
      })
      const updated = { ...prev, modules: newModules }
      debouncedSave({ modules: newModules })
      return updated
    })
  }, [debouncedSave])

  // Toggle module enabled
  const toggleModule = useCallback((moduleId) => {
    setProposal(prev => {
      if (!prev) return prev
      const newModules = prev.modules.map(m => {
        if (m.moduleId !== moduleId) return m
        return { ...m, enabled: !m.enabled }
      })
      const updated = { ...prev, modules: newModules }
      debouncedSave({ modules: newModules })
      return updated
    })
  }, [debouncedSave])

  // Reorder modules
  const reorderModules = useCallback((fromIndex, toIndex) => {
    setProposal(prev => {
      if (!prev) return prev
      const newModules = [...prev.modules]
      const [moved] = newModules.splice(fromIndex, 1)
      newModules.splice(toIndex, 0, moved)
      const reordered = newModules.map((m, i) => ({ ...m, order: i }))
      const updated = { ...prev, modules: reordered }
      debouncedSave({ modules: reordered })
      return updated
    })
  }, [debouncedSave])

  // Update CRM snapshot (single field)
  const updateCrmSnapshot = useCallback((field, value) => {
    setProposal(prev => {
      if (!prev) return prev
      const newSnapshot = { ...prev.crmSnapshot, [field]: value }
      const updated = { ...prev, crmSnapshot: newSnapshot }
      debouncedSave({ crmSnapshot: newSnapshot })
      return updated
    })
  }, [debouncedSave])

  // Bulk update multiple CRM snapshot fields at once (avoids debounce race conditions)
  const updateCrmSnapshots = useCallback((fieldsObj) => {
    setProposal(prev => {
      if (!prev) return prev
      const newSnapshot = { ...prev.crmSnapshot, ...fieldsObj }
      const updated = { ...prev, crmSnapshot: newSnapshot }
      debouncedSave({ crmSnapshot: newSnapshot })
      return updated
    })
  }, [debouncedSave])

  // Update jobsiteId
  const updateJobsite = useCallback((jobsiteId) => {
    setProposal(prev => {
      if (!prev) return prev
      const updated = { ...prev, jobsiteId: jobsiteId || null }
      debouncedSave({ jobsiteId: jobsiteId || null })
      return updated
    })
  }, [debouncedSave])

  // Update status
  const updateStatus = useCallback((status) => {
    setProposal(prev => {
      if (!prev) return prev
      const updated = { ...prev, status }
      saveProposal({ status })
      return updated
    })
  }, [saveProposal])

  // Get merged data for a module (defaults + overrides)
  const getModuleData = useCallback((moduleId) => {
    if (moduleId === 'scopeDetailSource') {
      const registry = moduleRegistry.find(m => m.id === 'scope')
      const mod = proposal?.modules?.find(m => m.moduleId === 'scope')
      return { ...(registry?.defaults || {}), ...(mod?.overrides || {}) }
    }
    if (hasEstimateContract(proposal?.estimate)) {
      if (moduleId === 'scope') return buildScopeData(proposal.estimate)
      if (moduleId === 'project') return buildProjectData(proposal.estimate)
      if (moduleId === 'executiveSummary') {
        const registry = moduleRegistry.find(m => m.id === 'executiveSummary')
        const mod = proposal?.modules?.find(m => m.moduleId === 'executiveSummary')
        return {
          ...(registry?.defaults || {}),
          ...(mod?.overrides || {}),
          ...buildExecSummaryData(proposal.estimate),
        }
      }
    }
    const registry = moduleRegistry.find(m => m.id === moduleId)
    const mod = proposal?.modules?.find(m => m.moduleId === moduleId)
    return { ...(registry?.defaults || {}), ...(mod?.overrides || {}) }
  }, [proposal, moduleRegistry])

  // ── Variables ─────────────────────────────────────────────────

  const resolvedVariables = useMemo(() => {
    return mergeEstimateVariables(
      resolveVariables(template, proposal),
      buildEstimateVariables(proposal?.estimate),
      proposal?.variables
    )
  }, [template, proposal])

  const variableDefinitions = useMemo(() => {
    if (!hasEstimateContract(proposal?.estimate)) return VARIABLE_DEFINITIONS
    return VARIABLE_DEFINITIONS.filter(def => !ESTIMATE_OWNED_VARIABLES.has(def.key))
  }, [proposal?.estimate])

  const updateVariable = useCallback((key, value) => {
    setProposal(prev => {
      if (!prev) return prev
      const newVars = { ...(prev.variables || {}) }
      if (value === undefined) {
        delete newVars[key]
      } else {
        newVars[key] = value
      }
      const updated = { ...prev, variables: newVars }
      debouncedSave({ variables: newVars })
      return updated
    })
  }, [debouncedSave])

  const resetVariables = useCallback(() => {
    setProposal(prev => {
      if (!prev) return prev
      const updated = { ...prev, variables: {} }
      debouncedSave({ variables: {} })
      return updated
    })
  }, [debouncedSave])

  // Manual save (for save button)
  const forceSave = useCallback(() => {
    if (!proposal) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    pendingSave.current = null
    saveProposal({
      modules: proposal.modules,
      crmSnapshot: proposal.crmSnapshot,
      status: proposal.status,
      variables: proposal.variables || {}
    })
  }, [proposal, saveProposal])

  return {
    proposal,
    template,
    loading,
    saving,
    dirty,
    error,
    updateModuleOverride,
    updateModuleOverrides,
    toggleModule,
    reorderModules,
    updateCrmSnapshot,
    updateCrmSnapshots,
    updateJobsite,
    updateStatus,
    getModuleData,
    forceSave,
    fetchProposal,
    moduleRegistry,
    // Variables
    variableDefinitions,
    resolvedVariables,
    updateVariable,
    resetVariables
  }
}

export default useProposal
